import { useCallback, useContext, useEffect, useState } from 'react';
import ImportSynchTripResultContext from './context';
import { SynchTripResultImportDTO } from 'business/models/Models';
import SynchTripService from 'business/services/SynchrotronTripService';
import { useSynchTrip } from '../hooks';

export const useMapData = () => {
  const synchTripContext = useContext(ImportSynchTripResultContext);

  useEffect(() => {
    if (synchTripContext.fileContent) {
      const dataWithoutHeaderRow = synchTripContext.fileContent.slice(1);
      const mappedData = dataWithoutHeaderRow
        .map((st) => {
          if (st.length <= 1) {
            return null;
          }
          const [
            crystalId,
            crystalName,
            puckBarcode,
            puckPinPostion,
            synchTripId,
            synchTripName,
            dateFrozen,
            crystalNameInternal,
            symm,
            xtalRun,
            resoLow,
            hiShells,
            cellA,
            cellB,
            cellC,
            cellAlpha,
            cellBeta,
            cellGamma,
            raddamRFree,
            e21,
            compEll,
            compSph,
            rMeas,
            foFC,
            multiplicity,
            cc12,
            iSigma,
            refinementProgram,
            resultNotes,
            resultStatus,
          ] = st;

          return new SynchTripResultImportDTO({
            id: 0,
            crystalId,
            crystalName,
            puckBarcode,
            puckPinPostion,
            synchTripId,
            synchTripName,
            dateFrozen,
            crystalNameInternal,
            symm,
            xtalRun,
            resoLow,
            hiShells,
            cellA,
            cellB,
            cellC,
            cellAlpha,
            cellBeta,
            cellGamma,
            raddamRFree,
            e21,
            compEll,
            compSph,
            rMeas,
            foFC,
            multiplicity,
            cc12,
            iSigma,
            refinementProgram,
            resultNotes,
            resultStatus,
            isDataInvalid: false,
            validationMessage: '',
            crystalTripId: 0,
          });
        })
        .filter((item) => item !== null);
      synchTripContext.setSynchTripResultImportDTOList(mappedData);
    }
  }, [synchTripContext.fileContent]);
};

export const useSaveTripResults = (refetchCollectedCrystals: () => void) => {
  const synchTripContext = useContext(ImportSynchTripResultContext);
  const synchTripService: SynchTripService = new SynchTripService();
  const synchTripId = window.location.pathname.split('/').filter(Boolean).pop();
  const { refetchSynchTrip } = useSynchTrip(parseInt(synchTripId, 10));

  const refetchCallback = useCallback(() => {
    refetchSynchTrip();
    refetchCollectedCrystals();
  }, []);

  useEffect(() => {
    const saveTripResults = async () => {
      await synchTripService.saveSynchTripResultImportData(
        synchTripContext.synchTripResultImportDTOList?.filter((x) => !x.isDataInvalid),
      );
      refetchCallback();
    };

    saveTripResults();
  }, [synchTripContext.synchTripResultImportDTOList, refetchCallback]);
};

export const useValidateData = () => {
  const [synchTripResult, setSynchTripResult] = useState<SynchTripResultImportDTO[]>();
  const synchTripContext = useContext(ImportSynchTripResultContext);

  const validateData = async () => {
    const synchTripService: SynchTripService = new SynchTripService();
    const res = await synchTripService.verifySynchTripResultImportData(
      synchTripContext.synchTripResultImportDTOList,
    );
    setSynchTripResult(res);
    synchTripContext.setSynchTripResultImportDTOList(res);
  };

  useEffect(() => {
    const fetchData = async () => {
      synchTripContext.setNextButtonDisallowed(true);
      await validateData();
      synchTripContext.setNextButtonDisallowed(false);
    };
    fetchData();
  }, []);

  return synchTripResult;
};
